import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MedicineService } from 'src/core/services/medicine.service';
import { DosageUnit } from 'src/interfaces/medicine';

@Component({
  selector: 'core-dosage-unit-manage-list',
  templateUrl: './dosage-unit-manage-list.component.html',
  styleUrls: ['./dosage-unit-manage-list.component.scss'],
  standalone: false
})
export class DosageUnitManageListComponent implements OnInit, OnDestroy {

  @Output() dosageUnitsChange = new EventEmitter<DosageUnit[]>();

  dosageUnits: DosageUnit[] = [];
  searchControl = new FormControl('');
  isSearching = false;

  private readonly destroy$ = new Subject<void>();
  private readonly searchLimit = 50;

  constructor(private _medicineService: MedicineService) { }

  ngOnInit(): void {
    this.loadDosageUnits('');

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((search: string | null) => {
        this.loadDosageUnits(search ?? '');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadDosageUnits(search: string): Promise<void> {
    this.isSearching = true;

    try {
      this.dosageUnits = await this._medicineService.getDosageUnits(1, this.searchLimit, search);
      this.dosageUnitsChange.emit(this.dosageUnits);
    } finally {
      this.isSearching = false;
    }
  }

  async createDosageUnit(): Promise<void> {
    const newDosageUnit: DosageUnit = await this._medicineService
      .openDosageUnitCreateDialog(
        'New dosage unit',
        'Create a new dosage unit'
      );

    if (!newDosageUnit) {
      return;
    }

    await this.loadDosageUnits(this.searchControl.value ?? '');
  }

  async deleteDosageUnit(unit: DosageUnit): Promise<void> {
    const success: boolean = await this._medicineService.deleteDosageUnit(unit.id);

    if (success) {
      this.dosageUnits = this.dosageUnits.filter(dosageUnit => dosageUnit.id !== unit.id);
      this.dosageUnitsChange.emit(this.dosageUnits);
    }
  }

}
